// The Reports tab — routes /reports and /reports/:reportId.
//
// Three reports over one shell (docs/REPORTS-TAB-DESIGN.md §4.3): Volume and mix,
// First reply, Backlog. The shell is a structural sibling of pages/tickets/List.jsx —
// same sidebar-plus-column layout, same loading, empty and error surfaces — so the
// app has one layout idea rather than two. The sidebar IS the extensibility: a
// fourth report is one entry in REPORTS, one panel, and nothing else.
//
// NO CHARTING LIBRARY, and the reason is theming rather than policy. app.css RULE 1:
// Tailwind v4 compiles `bg-surface` to `var(--color-surface)`, "so the token flips
// itself, with NO `dark:` variant at the call site". A <div> bar inherits that for
// free; a recharts `<Bar fill>` or a raw SVG `fill` does not, and the whole palette
// would have to be duplicated in JS and re-solved for dark mode — which the
// developer, who runs this app in dark mode, would find first. None of these three
// reports needs a continuous axis or a curve, so a flex track with percentage
// segments is the entire vocabulary. `style={{ width }}` is the only inline style
// here and it is unavoidable: the value is data, and Tailwind cannot generate an
// arbitrary runtime percentage.
//
// EVERY NUMBER ON THIS SCREEN IS A COUNT OR A MEASURED DURATION. There is no
// percentage anywhere, and that is deliberate rather than incidental — see
// src/routes/api/reports.js and design §7 for what was refused and why.

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';

import { api } from '../../api/client.js';
import { StatusBadge } from '../../components/tickets/Badges.jsx';
import { CHANNEL_LABELS, formatDateTime } from '../../components/tickets/format.js';
import { toSearchParams as toQueueParams } from '../../components/tickets/queue-query.js';
import ReportBuilder from './ReportBuilder.jsx';
import {
  BUILDER,
  DEFAULT_REPORT,
  RANGE_PRESETS,
  TIMEZONES,
  effectiveWindow,
  isReportId,
  readReportParams,
  REPORTS,
  toApiQuery,
  toSearchParams,
} from './report-query.js';
import {
  CHANNELS,
  CHANNEL_GROUP_LABELS,
  answeredCount,
  barWidths,
  clockStates,
  fillChannelGroups,
  fillDays,
  formatDuration,
  maxDailyTotal,
  noteSentences,
  pivotVolume,
  quotable,
  shapeBacklog,
  volumeTotals,
} from './report-shape.js';
import { supportPath } from '../../support-path.js';

// The unsolved statuses, for the one drill-through that can express itself exactly.
const UNSOLVED = ['new', 'open', 'pending', 'on_hold'];

const RANGE_LABELS = { '7d': 'Last 7 days', '30d': 'Last 30 days', '90d': 'Last 90 days', custom: 'Custom' };

/** Today as a local 'YYYY-MM-DD', for resolving a preset into explicit dates. */
function todayLocal() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/** 'Sun 23 Aug' from a 'YYYY-MM-DD' string, without letting the browser shift it. */
function formatDay(day) {
  // Parsed as UTC and formatted as UTC. `new Date('2026-08-23')` is already UTC
  // midnight, so formatting it in the browser's zone would render 22 Aug anywhere
  // west of Greenwich — the local calendar day was decided in the database and must
  // not be re-interpreted here.
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
  });
}

// ---------------------------------------------------------------------------
// Shared furniture
// ---------------------------------------------------------------------------

/** ViewPicker's exact classes, rendering Links because each report is a real route. */
const ITEM_BASE = 'block w-full truncate rounded-md px-2 py-1.5 text-left text-sm transition-colors';
const ITEM_ACTIVE = 'bg-sunken font-medium text-ink';
const ITEM_IDLE = 'text-ink-secondary hover:bg-sunken hover:text-ink';

function ReportPicker({ active, search }) {
  return (
    <nav aria-label="Reports" className="rounded-lg border border-line bg-surface p-2">
      <ul className="space-y-0.5">
        {REPORTS.map((report) => (
          <li key={report.id}>
            <Link
              to={{ pathname: report.path, search: search.toString() }}
              aria-current={report.id === active ? 'true' : undefined}
              className={`${ITEM_BASE} ${report.id === active ? ITEM_ACTIVE : ITEM_IDLE}`}
            >
              {report.label}
            </Link>
          </li>
        ))}
        {/* Separated, because it is a different kind of thing: the three above are
            questions this desk has already decided it asks every week, and this one is
            where any other question gets asked. The window params ride along so going
            back to a fixed report lands on the range that was being looked at. */}
        <li className="pt-1">
          <Link
            to={{ pathname: BUILDER.path, search: search.toString() }}
            aria-current={BUILDER.id === active ? 'true' : undefined}
            className={`${ITEM_BASE} border-t border-line pt-2 ${BUILDER.id === active ? ITEM_ACTIVE : ITEM_IDLE}`}
          >
            {BUILDER.label}
          </Link>
        </li>
      </ul>
    </nav>
  );
}

function Card({ children, className = '' }) {
  return <div className={`rounded-lg border border-line bg-surface p-4 ${className}`}>{children}</div>;
}

function Tile({ label, value, hint }) {
  return (
    <Card>
      <p className="text-xs font-medium uppercase tracking-wide text-ink-secondary">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-ink">{value}</p>
      {hint && <p className="mt-1 text-xs text-ink-muted">{hint}</p>}
    </Card>
  );
}

/**
 * The caveats, in the note tokens — the only warm hue in the system, and already the
 * app's "read this" treatment for internal notes. No new colours.
 *
 * These are not decoration. Each sentence is the difference between a number and a
 * number somebody can act on, and they are rendered from codes the API emitted
 * alongside the rows so they cannot drift away from what they qualify.
 */
function Notes({ notes }) {
  const sentences = noteSentences(notes);
  if (sentences.length === 0) return null;
  return (
    <div className="rounded-lg border border-note-line bg-note p-3">
      <ul className="space-y-1">
        {sentences.map((sentence) => (
          <li key={sentence} className="text-sm text-note-ink">{sentence}</li>
        ))}
      </ul>
    </div>
  );
}

/** A count that is genuinely zero, de-emphasised — but promoted on row hover. */
function Count({ n, muted = false }) {
  // READ, List.jsx: text-ink-muted measures 4.76:1 on bg-surface but only 4.34:1 on
  // bg-sunken in light mode — i.e. below AA on a hovered row. Same fix, same reason.
  const tone = muted && n === 0
    ? 'text-ink-muted group-hover:text-ink-secondary'
    : 'text-ink';
  return <span className={`tabular-nums ${tone}`}>{n}</span>;
}

// ---------------------------------------------------------------------------
// Volume and mix
// ---------------------------------------------------------------------------

function VolumePanel({ payload, window: win }) {
  const days = useMemo(
    () => pivotVolume(fillDays(payload.data.rows, win.from, win.to, payload.meta.data_from_day)),
    [payload, win.from, win.to],
  );
  const totals = volumeTotals(days);
  const scale = maxDailyTotal(days);
  const horizon = payload.meta.data_from
    ? formatDay(payload.meta.data_from_day)
    : null;

  return (
    <>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Tile
          label="Created"
          value={totals.created.toLocaleString()}
          hint={totals.perDay === null ? 'No recorded days' : `${totals.perDay.toFixed(1)}/day over ${totals.recordedDays} recorded ${totals.recordedDays === 1 ? 'day' : 'days'}`}
        />
        <Tile label="Solved" value={totals.solved.toLocaleString()} />
        <Tile
          label="Net"
          value={`${totals.net > 0 ? '+' : ''}${totals.net.toLocaleString()}`}
          hint={totals.net > 0 ? 'Backlog grew' : totals.net < 0 ? 'Backlog shrank' : 'Level'}
        />
        <Tile
          label="Busiest day"
          value={totals.busiest ? formatDay(totals.busiest.day) : '-'}
          hint={totals.busiest ? `${totals.busiest.created.total} created` : undefined}
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-line bg-surface">
        <table className="w-full table-fixed divide-y divide-line text-sm">
          <colgroup>
            <col className="w-[7.5rem]" />
            <col />
            <col className="w-[4.5rem]" />
            <col className="w-[4.5rem]" />
            <col className="w-[4.5rem]" />
            <col className="w-[5rem]" />
          </colgroup>
          <thead className="bg-sunken text-left text-xs font-medium uppercase tracking-wide text-ink-secondary">
            <tr>
              <th scope="col" className="px-4 py-2.5">Day</th>
              <th scope="col" className="px-4 py-2.5">Created by channel</th>
              {CHANNELS.map((channel) => (
                <th key={channel} scope="col" className="px-4 py-2.5 text-right">
                  {CHANNEL_LABELS[channel]}
                </th>
              ))}
              <th scope="col" className="px-4 py-2.5 text-right">Solved</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {days.map((entry) => {
              if (entry.unrecorded) {
                // A day before the horizon is NOT zero. Drawing it as a zero-height
                // bar would invent a quiet desk that never existed — the single most
                // likely way this screen misleads someone, and it costs one branch.
                return (
                  <tr key={entry.day} className="text-ink-muted">
                    <td className="whitespace-nowrap px-4 py-2">{formatDay(entry.day)}</td>
                    <td
                      className="px-4 py-2 text-xs italic"
                      title={horizon ? `No data. This system's records begin ${horizon}` : 'No data recorded'}
                    >
                      not recorded
                      {/* `title` is not keyboard reachable, is unreliably announced,
                          and never appears on touch, so the horizon also goes in
                          sr-only text — the same belt-and-braces List.jsx uses for
                          its SLA column. The four em dashes below are then purely
                          visual and say nothing worth announcing. */}
                      <span className="sr-only">
                        {horizon ? `, this system's records begin ${horizon}` : ', no data recorded'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right" aria-hidden="true">-</td>
                    <td className="px-4 py-2 text-right" aria-hidden="true">-</td>
                    <td className="px-4 py-2 text-right" aria-hidden="true">-</td>
                    <td className="px-4 py-2 text-right" aria-hidden="true">-</td>
                  </tr>
                );
              }
              const widths = barWidths(CHANNELS.map((c) => entry.created[c]), scale);
              return (
                <tr key={entry.day} className="group transition-colors hover:bg-sunken">
                  <td className="whitespace-nowrap px-4 py-2 text-ink">{formatDay(entry.day)}</td>
                  <td className="px-4 py-2">
                    {/* aria-hidden: every number in this bar is in a real <td> beside
                        it. The bar is a second, visual encoding of data the row
                        already states in text, and a div has nothing useful to
                        announce. Segments are scaled against the BUSIEST day in the
                        window, not against this row's own total — so length means
                        volume and the ratio means mix. */}
                    <div aria-hidden="true" className="flex h-2 w-full overflow-hidden rounded bg-sunken">
                      <div className="bg-brand-600" style={{ width: `${widths[0]}%` }} />
                      <div className="bg-brand-400" style={{ width: `${widths[1]}%` }} />
                      <div className="bg-brand-200" style={{ width: `${widths[2]}%` }} />
                    </div>
                  </td>
                  {CHANNELS.map((channel) => (
                    <td key={channel} className="px-4 py-2 text-right">
                      <Count n={entry.created[channel]} muted />
                    </td>
                  ))}
                  <td className="px-4 py-2 text-right"><Count n={entry.solved} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// First reply
// ---------------------------------------------------------------------------

// Semantic colours rather than the brand ramp, so a state means the same thing here
// as it does in the queue. Fixed order: good, late, overdue, still in target.
const STATE_FILLS = {
  answered_in_target: 'bg-emerald-500',
  answered_late: 'bg-amber-500',
  unanswered_overdue: 'bg-red-500',
  unanswered_in_target: 'bg-brand-300',
};

function FirstReplyCard({ group }) {
  const states = clockStates(group);
  const widths = barWidths(states.map((state) => state.n), group.clocks ?? 0);
  const answered = answeredCount(group);
  const canQuote = quotable(answered);

  return (
    <Card>
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-sm font-semibold text-ink">
          {CHANNEL_GROUP_LABELS[group.channel_group] ?? group.channel_group}
        </h2>
        <p className="text-xs text-ink-muted">
          {group.target_min_low === null ? 'No clocks' : `Target ${group.target_min_low} min`}
          {' · '}
          {group.clocks} {group.clocks === 1 ? 'clock' : 'clocks'}
          {group.no_clock > 0 && ` · ${group.no_clock} with no clock`}
        </p>
      </div>

      <div aria-hidden="true" className="mt-3 flex h-2 w-full overflow-hidden rounded bg-sunken">
        {states.map((state, index) => (
          <div key={state.key} className={STATE_FILLS[state.key]} style={{ width: `${widths[index]}%` }} />
        ))}
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
        {states.map((state) => (
          <div key={state.key}>
            <dt className="text-xs text-ink-secondary">{state.label}</dt>
            <dd className="tabular-nums text-ink">{state.n}</dd>
          </div>
        ))}
      </dl>

      <p className="mt-3 text-sm text-ink-secondary">
        Median reply{' '}
        {canQuote ? (
          <>
            <span className="tabular-nums text-ink">{formatDuration(group.p50_sec)}</span>
            <span className="text-ink-muted">
              {' · '}90th percentile <span className="tabular-nums">{formatDuration(group.p90_sec)}</span>
            </span>
          </>
        ) : (
          // BELOW THE FLOOR THE NUMBER IS NOT RENDERED AT ALL — not greyed, not in a
          // smaller font. It is replaced by the sentence, because a median over
          // seven clocks quoted quietly is still a median somebody will repeat.
          <span className="text-ink-muted">
            n = {answered}, too few to quote a median
          </span>
        )}
      </p>
    </Card>
  );
}

function FirstReplyPanel({ payload }) {
  const groups = fillChannelGroups(payload.data.groups);
  return (
    <div className="space-y-4">
      {groups.map((group) => <FirstReplyCard key={group.channel_group} group={group} />)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Backlog
// ---------------------------------------------------------------------------

function BacklogTable({ title, groupLabel, rows, caption, renderLabel, hrefFor }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-surface">
      <table className="w-full table-fixed divide-y divide-line text-sm">
        <caption className="px-4 pb-2 pt-3 text-left text-xs font-medium uppercase tracking-wide text-ink-secondary">
          {title}
          {caption && <span className="ml-2 font-normal normal-case text-ink-muted">{caption}</span>}
        </caption>
        <colgroup>
          <col />
          <col className="w-[4rem]" />
          <col className="w-[9rem]" />
        </colgroup>
        <thead className="bg-sunken text-left text-xs font-medium uppercase tracking-wide text-ink-secondary">
          <tr>
            <th scope="col" className="px-4 py-2.5">{groupLabel}</th>
            <th scope="col" className="px-4 py-2.5 text-right">Open</th>
            <th scope="col" className="px-4 py-2.5">Oldest</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {rows.map((row) => {
            const href = hrefFor ? hrefFor(row) : null;
            return (
              <tr key={`${row.bucket}`} className="group transition-colors hover:bg-sunken">
                <td className="truncate px-4 py-2">
                  {/* A number links ONLY where the queue can reproduce the same set
                      exactly. Age and date cannot be expressed by GET /api/tickets,
                      so those rows are plain text — not a disabled-looking link, and
                      never a link that quietly drops a filter and lands on a
                      different number than the one that was clicked. */}
                  {href
                    ? <Link to={href} className="text-brand-600 hover:underline dark:text-brand-400">{renderLabel(row)}</Link>
                    : renderLabel(row)}
                </td>
                <td className="px-4 py-2 text-right"><Count n={row.n} muted /></td>
                <td className="whitespace-nowrap px-4 py-2 text-xs text-ink-muted group-hover:text-ink-secondary">
                  {row.oldest_created_at ? formatDateTime(row.oldest_created_at) : '-'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BacklogPanel({ payload }) {
  const shaped = shapeBacklog(payload.data.rows);
  return (
    <div className="space-y-4">
      <BacklogTable
        title="By age"
        groupLabel="Age"
        caption="since created"
        rows={shaped.age}
        renderLabel={(row) => <span className="text-ink">{row.label}</span>}
      />
      <BacklogTable
        title="By assignee"
        groupLabel="Assignee"
        rows={shaped.assignee}
        renderLabel={(row) => (
          <span className={row.bucket === null ? 'text-ink-secondary' : 'text-ink'}>
            {row.label ?? `User ${row.bucket}`}
          </span>
        )}
        hrefFor={(row) => (row.bucket === null ? null : {
          pathname: supportPath('/tickets'),
          // Built with the queue's OWN serialiser, so there is one definition of a
          // queue URL rather than two that drift.
          search: toQueueParams({
            view: '', statuses: UNSOLVED, channels: [],
            assignee: row.bucket, q: '', sort: '', offset: 0,
          }).toString(),
        })}
      />
      <BacklogTable
        title="By status"
        groupLabel="Status"
        rows={shaped.status}
        renderLabel={(row) => <StatusBadge status={row.bucket} namedStatus={row.label ? { name: row.label } : null} />}
        hrefFor={(row) => ({
          pathname: supportPath('/tickets'),
          search: toQueueParams({
            view: '', statuses: [row.bucket], channels: [],
            assignee: '', q: '', sort: '', offset: 0,
          }).toString(),
        })}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// The screen
// ---------------------------------------------------------------------------

function RangeControl({ params, onChange, disabled }) {
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-line bg-surface p-3">
      <div>
        <label className="field-label" htmlFor="report-range">Range</label>
        <select
          id="report-range"
          className="field-input"
          value={params.range}
          disabled={disabled}
          onChange={(event) => onChange({ range: event.target.value })}
        >
          {RANGE_PRESETS.map((preset) => (
            <option key={preset} value={preset}>{RANGE_LABELS[preset]}</option>
          ))}
        </select>
      </div>

      {params.range === 'custom' && (
        <>
          <div>
            <label className="field-label" htmlFor="report-from">From</label>
            <input
              id="report-from" type="date" className="field-input" value={params.from}
              disabled={disabled} onChange={(event) => onChange({ from: event.target.value })}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="report-to">To</label>
            <input
              id="report-to" type="date" className="field-input" value={params.to}
              disabled={disabled} onChange={(event) => onChange({ to: event.target.value })}
            />
          </div>
        </>
      )}

      <div>
        <label className="field-label" htmlFor="report-tz">Timezone</label>
        <select
          id="report-tz"
          className="field-input"
          value={params.tz}
          onChange={(event) => onChange({ tz: event.target.value })}
        >
          {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
        </select>
      </div>
    </div>
  );
}

export default function Reports() {
  const { reportId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // The builder is a sibling screen inside the same shell, not a fourth fixed report: it
  // owns its own request, its own window state and its own panel. Every hook below still
  // runs — an early return before them would be a hook-order change between routes — and
  // `ready` is what keeps the shell's fetch from asking for `/api/reports/builder`.
  const builder = reportId === BUILDER.id;
  const active = isReportId(reportId) ? reportId : DEFAULT_REPORT;
  const params = useMemo(() => readReportParams(searchParams), [searchParams]);
  const today = todayLocal();
  const queryString = toApiQuery(params, today, active);

  const report = REPORTS.find((entry) => entry.id === active);
  const win = effectiveWindow(params, today);
  // A half-entered custom range is not a window. Asking anyway would have the server
  // fall back to its own default and return a report for a week nobody selected —
  // whose caveats would then render under a panel that is hidden. Better to ask for
  // the missing date than to answer a question that was not put.
  const ready = !builder && (active === 'backlog' || win !== null);

  const [result, setResult] = useState({ status: 'loading', payload: null, error: null });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!ready) {
      setResult({ status: 'idle', payload: null, error: null });
      return undefined;
    }
    const controller = new AbortController();
    setResult((previous) => ({ ...previous, status: 'loading', error: null }));

    api.get(`/reports/${active}?${queryString}`, { signal: controller.signal })
      .then((payload) => setResult({ status: 'ready', payload, error: null }))
      .catch((error) => {
        if (controller.signal.aborted || error.name === 'AbortError') return;
        setResult({ status: 'error', payload: null, error });
      });

    return () => controller.abort();
  }, [active, queryString, reloadToken, ready]);

  const update = (patch) => setSearchParams(toSearchParams({ ...params, ...patch }));

  const payload = result.payload;

  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
      <aside className="lg:w-56 lg:shrink-0">
        <ReportPicker active={builder ? BUILDER.id : active} search={toSearchParams(params)} />
      </aside>

      {builder && <div className="min-w-0 flex-1"><ReportBuilder /></div>}

      {!builder && (
      <div className="min-w-0 flex-1 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-xl font-semibold tracking-tight">{report.label}</h1>
          {payload && (
            // The window that was ACTUALLY queried, echoed from the response rather
            // than recomputed here — a caption that describes a window nobody asked
            // for is the same class of lie as a wrong number.
            <p className="text-sm text-ink-muted">
              {payload.meta.window
                ? `${payload.meta.window.from} to ${payload.meta.window.to} · ${payload.meta.window.timezone}`
                : `As at ${formatDateTime(payload.meta.generated_at)}`}
            </p>
          )}
        </div>

        {active !== 'backlog' && (
          <RangeControl params={params} onChange={update} disabled={result.status === 'loading'} />
        )}

        {result.status === 'error' && (
          // Copied from List.jsx, including the explicit dark pair: no semantic
          // error-surface token exists and this is not the place to invent a second
          // error surface.
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
            <p className="text-sm font-medium text-red-800 dark:text-red-200">
              Could not load this report.
            </p>
            <p className="mt-1 text-sm text-red-700 dark:text-red-300">{result.error?.message}</p>
            <button
              type="button"
              className="btn-ghost mt-3 ring-1 ring-red-300 dark:ring-red-800"
              onClick={() => setReloadToken((token) => token + 1)}
            >
              Try again
            </button>
          </div>
        )}

        {result.status === 'loading' && !payload && (
          <Card><p className="text-sm text-ink-muted">Loading…</p></Card>
        )}

        {!ready && (
          <Card><p className="text-sm text-ink-muted">Choose a start and end date.</p></Card>
        )}

        {payload && ready && active === 'volume' && win && <VolumePanel payload={payload} window={win} />}
        {payload && active === 'first-reply' && <FirstReplyPanel payload={payload} />}
        {payload && active === 'backlog' && <BacklogPanel payload={payload} />}

        {payload && <Notes notes={payload.meta.notes} />}
      </div>
      )}
    </div>
  );
}
